import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useWorkflowStore } from '../admin/store/workflowStore';
import type { WorkflowWarranty } from '../admin/types/workflow';

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
      active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
    }`}>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {label}
    </span>
  );
}

function fmtDate(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' });
}

function isExpired(expiry: string) {
  return expiry ? new Date(expiry) < new Date() : false;
}

export default function WarrantyVerify() {
  const { warrantyId } = useParams<{ warrantyId: string }>();
  const { getWarrantyByVerifyId } = useWorkflowStore();
  const [warranty, setWarranty] = useState<WorkflowWarranty | null | undefined>(undefined);

  useEffect(() => {
    if (warrantyId) {
      const found = getWarrantyByVerifyId(warrantyId);
      setWarranty(found || null);
    }
  }, [warrantyId, getWarrantyByVerifyId]);

  const productActive = warranty ? !isExpired(warranty.productWarrantyExpiry) : false;
  const serviceActive = warranty ? !isExpired(warranty.serviceWarrantyExpiry) : false;

  if (warranty === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-[#7FA86A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (warranty === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Warranty Not Found</h2>
          <p className="text-gray-500 text-sm mb-6">The warranty reference <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{warrantyId}</code> could not be found.</p>
          <Link to="/" className="text-[#7FA86A] text-sm font-medium hover:underline">← Back to K-Tec Website</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-[#7FA86A] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold">K</span>
          </div>
          <div>
            <h1 className="font-bold text-gray-900 text-sm">K-TEC ENTERPRISE</h1>
            <p className="text-xs text-gray-500">Warranty & Service Verification</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Reference Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Warranty Reference</p>
              <p className="font-bold text-gray-900 font-mono text-lg">{warranty.warrantyId}</p>
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              warranty.warrantyStatus === 'active' ? 'bg-emerald-100' : 'bg-red-100'
            }`}>
              <svg className={`w-6 h-6 ${warranty.warrantyStatus === 'active' ? 'text-emerald-600' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Customer</p>
              <p className="font-semibold text-gray-900">{warranty.customerName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="font-semibold text-gray-900">{warranty.customerPhone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Installation Date</p>
              <p className="font-medium text-gray-700">{fmtDate(warranty.installationDate)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Invoice Ref</p>
              <p className="font-medium text-gray-700 font-mono text-xs">{warranty.invoiceNumber}</p>
            </div>
          </div>
        </div>

        {/* Product Warranty Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Product Warranty</h2>
            <StatusBadge active={productActive} label={productActive ? 'In Warranty' : 'Expired'} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-semibold text-gray-900">{warranty.productWarrantyDuration}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Start Date</p>
              <p className="font-medium text-gray-700">{fmtDate(warranty.productWarrantyStart)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Expiry Date</p>
              <p className={`font-semibold ${productActive ? 'text-emerald-600' : 'text-red-600'}`}>
                {fmtDate(warranty.productWarrantyExpiry)}
              </p>
            </div>
          </div>
        </div>

        {/* Service Warranty Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">K-Tec Service Support</h2>
            <StatusBadge active={serviceActive} label={serviceActive ? 'Support Active' : 'Expired'} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-semibold text-gray-900">{warranty.serviceWarrantyDuration}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Start Date</p>
              <p className="font-medium text-gray-700">{fmtDate(warranty.serviceWarrantyStart)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Expiry Date</p>
              <p className={`font-semibold ${serviceActive ? 'text-emerald-600' : 'text-red-600'}`}>
                {fmtDate(warranty.serviceWarrantyExpiry)}
              </p>
            </div>
          </div>
        </div>

        {/* Installed Equipment */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Installed Equipment</h2>
          {warranty.items.length === 0 ? (
            <p className="text-sm text-gray-400">No equipment recorded.</p>
          ) : (
            <div className="space-y-3">
              {warranty.items.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{item.productName}</p>
                      {item.brand && <p className="text-xs text-gray-500 mt-0.5">{item.brand}</p>}
                    </div>
                    <span className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-full text-gray-600 ml-2">
                      Qty: {item.quantity}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    {item.modelNumber && (
                      <div>
                        <span className="text-gray-500">Model: </span>
                        <span className="font-medium text-gray-700">{item.modelNumber}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Warranty: </span>
                      <span className="font-medium text-gray-700">{item.warrantyPeriod}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Expires: </span>
                      <span className={`font-semibold ${isExpired(item.warrantyExpiry) ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmtDate(item.warrantyExpiry)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Support Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Need Support?</h2>
          <div className="grid grid-cols-3 gap-3">
            <a
              href="tel:+601112345678"
              className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-center"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-700">Call Support</span>
            </a>
            <a
              href="https://wa.me/601112345678?text=Hi+K-Tec%2C+I+need+support+for+my+warranty."
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-center"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.121.55 4.11 1.517 5.843L.057 23.41l5.716-1.494A11.946 11.946 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.007-1.374l-.359-.213-3.728.976 1.001-3.644-.234-.376A9.818 9.818 0 112 12c0 5.42 4.4 9.818 9.818 9.818h.182z"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-700">WhatsApp</span>
            </a>
            <a
              href="mailto:support@ktec.com.my?subject=Service Request"
              className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-center"
            >
              <div className="w-10 h-10 bg-[#E8F2E3] rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-[#7FA86A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-700">Request Service</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <Link to="/" className="text-sm text-[#7FA86A] font-medium hover:underline">
            K-Tec Enterprise — www.ktec.com.my
          </Link>
          <p className="text-xs text-gray-400 mt-1">This page is for warranty verification only. No financial information is shown.</p>
        </div>
      </div>
    </div>
  );
}
